import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { Box, Grid, TextField, IconButton, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import useApiCall from "../common/api-call";
import { getUserStatistics } from "./statistics-service";

const UserStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getUserStatistics(apiCall, start, end);

      // Handle new response structure with statistics array
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for old response format
        setFilteredData(response);
      } else {
        setFilteredData([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  // Prepare data for both charts
  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      userCount: 0,
      dailyActiveUserCount: 0,
    };
  });

  filteredData?.forEach(item => {
    const dateIndex = dayData.findIndex(day => day.day === item.date);
    if (dateIndex !== -1) {
      dayData[dateIndex].userCount += item.userCount || 0;
      dayData[dateIndex].dailyActiveUserCount += item.dailyActiveUserCount || 0;
    }
  });

  const maxUserCount = Math.max(...dayData.map(item => item.userCount), 0);
  const maxDailyActiveUsers = Math.max(
    ...dayData.map(item => item.dailyActiveUserCount),
    0,
  );
  const userCountYAxisMax =
    maxUserCount > 0 ? Math.ceil(maxUserCount * 1.1) : 40;
  const dailyActiveYAxisMax =
    maxDailyActiveUsers > 0 ? Math.ceil(maxDailyActiveUsers * 1.1) : 40;

  // Chart options for User Count
  const getUserCountChartOption = () => ({
    title: {
      text: "Total Users",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name, value } = params[0];
        return `Date: ${name}<br />User Count: ${value}`;
      },
    },
    legend: {
      data: ["User Count"],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: userCountYAxisMax,
      interval: Math.max(1, Math.ceil(userCountYAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "User Count",
        type: "line",
        data: dayData.map(item => item.userCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#4caf50",
        },
      },
    ],
  });

  // Chart options for Daily Active Users
  const getDailyActiveUsersChartOption = () => ({
    title: {
      text: "Daily Active Users",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name, value } = params[0];
        return `Date: ${name}<br />Daily Active Users: ${value}`;
      },
    },
    legend: {
      data: ["Daily Active Users"],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: dailyActiveYAxisMax,
      interval: Math.max(1, Math.ceil(dailyActiveYAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "Daily Active Users",
        type: "line",
        data: dayData.map(item => item.dailyActiveUserCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#2196f3",
        },
      },
    ],
  });

  useEffect(() => {
    // Initialize User Count Chart
    const userCountChartDom = document.getElementById("user-count-chart");
    const userCountChart = echarts.init(userCountChartDom);
    userCountChart.setOption(getUserCountChartOption());

    // Initialize Daily Active Users Chart
    const dailyActiveChartDom = document.getElementById(
      "daily-active-users-chart",
    );
    const dailyActiveChart = echarts.init(dailyActiveChartDom);
    dailyActiveChart.setOption(getDailyActiveUsersChartOption());

    const handleResize = () => {
      userCountChart.resize();
      dailyActiveChart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      userCountChart.dispose();
      dailyActiveChart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredData, startDate, endDate]);

  return (
    <Box sx={{ flexGrow: 1, height: "100%", m: 2, pb: "80px" }}>
      {/* Date Selection Controls */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
          Date Range Selection
        </Typography>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
          >
            <IconButton
              onClick={handleFetchData}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                ml: 1,
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* User Count Chart */}
      <Box sx={{ width: "100%", height: { xs: "300px", sm: "400px" }, mb: 3 }}>
        <Box id="user-count-chart" style={{ width: "100%", height: "100%" }} />
      </Box>

      {/* Daily Active Users Chart */}
      <Box sx={{ width: "100%", height: { xs: "300px", sm: "400px" } }}>
        <Box
          id="daily-active-users-chart"
          style={{ width: "100%", height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default UserStatisticsView;
